import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { useSeason } from '../contexts/SeasonContext';
import { SeasonSelector } from '../components/common/SeasonSelector';
import { Trophy, Star } from 'lucide-react';

export default function TeamsPage() {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const { currentSeason, defaultSeason } = useSeason();
    const showDefunct = currentSeason !== defaultSeason;

    useEffect(() => {
        API.get(`/teams?include_defunct=${showDefunct}`).then(res => {
            setTeams(res.data.teams || []);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [showDefunct]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="teams-page">
            <div className="glass-card p-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Trophy className="w-5 h-5 text-yellow-400" />
                        <h2 className="heading-primary text-base md:text-xl text-white">IPL TEAMS ANALYTICS</h2>
                    </div>
                    <SeasonSelector />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teams.map((team, i) => (
                    <div key={team.id} data-testid={`team-card-${team.id}`}
                        className={`glass-card p-6 animate-fadeInUp stagger-${(i % 6) + 1} transition-all`}
                        style={{ borderColor: `${team.color}15` }}>
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <div className="heading-primary text-lg text-white">{team.name}</div>
                                <div className="tech-mono text-xs mt-1" style={{ color: team.color }}>{team.short_name}</div>
                            </div>
                            <div className="w-12 h-12 rounded-lg flex items-center justify-center text-xl font-black"
                                style={{ backgroundColor: `${team.color}20`, color: team.color }}>
                                {team.short_name?.charAt(0)}
                            </div>
                        </div>
                        <div className="mb-4">
                            <div className="flex justify-between mb-1">
                                <span className="tech-mono text-[10px] text-white/30">TEAM STRENGTH</span>
                                <span className="heading-primary text-sm" style={{ color: team.color }}>{team.strength_score}</span>
                            </div>
                            <div className="prob-bar">
                                <div className="prob-bar-fill" style={{ width: `${team.strength_score}%`, backgroundColor: team.color }} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="tech-mono text-[10px] text-white/30">WIN RATE</div>
                                <div className="text-sm text-white font-bold">{(team.win_rate * 100).toFixed(0)}%</div>
                            </div>
                            <div>
                                <div className="tech-mono text-[10px] text-white/30">NRR</div>
                                <div className="text-sm text-white font-bold">{team.nrr > 0 ? '+' : ''}{team.nrr}</div>
                            </div>
                            <div>
                                <div className="tech-mono text-[10px] text-white/30">PLAYED</div>
                                <div className="text-sm text-white font-bold">{team.historical?.total_matches || team.matches_played}</div>
                            </div>
                            <div>
                                <div className="tech-mono text-[10px] text-white/30">TITLES</div>
                                <div className="text-sm font-bold" style={{ color: team.titles > 0 ? '#FFD600' : '#666' }}>
                                    {team.titles > 0 ? `${team.titles}x` : '-'}
                                </div>
                            </div>
                        </div>
                        {team.historical && (
                            <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-2 gap-2">
                                <div>
                                    <div className="tech-mono text-[10px] text-white/20">AVG SCORE</div>
                                    <div className="text-xs text-cyan-400 font-bold">{team.historical.avg_score}</div>
                                </div>
                                <div>
                                    <div className="tech-mono text-[10px] text-white/20">AVG CONCEDED</div>
                                    <div className="text-xs text-red-400 font-bold">{team.historical.avg_conceded}</div>
                                </div>
                            </div>
                        )}
                        {team.impact_player?.recommended && (
                            <div className="mt-3 pt-3 border-t border-white/5" data-testid={`impact-player-${team.id}`}>
                                <div className="flex items-center gap-1 mb-1.5">
                                    <Star className="w-3 h-3 text-orange-400" />
                                    <span className="tech-mono text-[10px] text-orange-400 font-bold">IMPACT PLAYER</span>
                                </div>
                                <div className="flex items-center justify-between p-2 bg-orange-500/5 border border-orange-500/10 rounded-lg">
                                    <div>
                                        <div className="text-xs text-white/80 font-medium">{team.impact_player.recommended.player_name}</div>
                                        <div className="text-[9px] text-white/30">{team.impact_player.recommended.impact_role} impact | {team.impact_player.recommended.role}</div>
                                    </div>
                                    <div className="text-green-400 font-bold text-sm">+{team.impact_player.recommended.win_pct_shift}%</div>
                                </div>
                            </div>
                        )}
                        {team.defunct && (
                            <div className="mt-3 pt-3 border-t border-white/5">
                                <div className="text-[10px] text-amber-400/80 tech-mono">DEFUNCT — {team.active_years}</div>
                            </div>
                        )}
                        {!team.defunct && (
                            <div className="mt-3 pt-3 border-t border-white/5">
                                <div className="text-xs text-white/40">Captain: <span className="text-white/80">{team.captain}</span></div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
